import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { HttpStatusCode } from "axios";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import LoadingButton from "_shared/_views/loading/loading-button";
import { useOverrideDocumentClassificationMutation } from "documents/_api/document-api";
import Document from "documents/document";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";

const SectionLabel = styled(FormLabel)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
  "&.Mui-focused": { color: theme.palette.text.primary },
})) as typeof FormLabel;

const subjectMatters = Object.values(DocumentSubjectMatter).filter(
  (value): value is DocumentSubjectMatter => typeof value === "number",
);

export const contentChangedMessage =
  "This document changed after you opened it, so the subject matter shown may be out of date. "
  + "What it carries now is shown below. Review it and save again.";

interface EditSubjectMattersDialogProps {
  /** The document as it is now. Pass the latest read, so a reload after a 409 reaches the form. */
  document: Document;
  onClose: () => void;
}

/**
 * Replaces a document's subject-matter labels, with a reason (#4030).
 *
 * The whole set is sent, not a change to it, together with the content hash the labels were read
 * against. Offered only where `Document.canOverrideClassification` says the API would accept it,
 * so a 403 here means the reader's access changed while the dialog was open.
 */
export default function EditSubjectMattersDialog({ document, onClose }: EditSubjectMattersDialogProps) {
  const [overrideClassification, { isLoading }] = useOverrideDocumentClassificationMutation();
  const [selected, setSelected] = useState<DocumentSubjectMatter[]>(document.subjectMatters ?? []);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  // The content version the checkboxes were filled from. When a later read brings another one -
  // after a 409, say - the form takes that read's labels, so nobody saves over a version they
  // never saw.
  const [loadedHash, setLoadedHash] = useState(document.classificationContentHash);
  if (document.classificationContentHash !== loadedHash) {
    setLoadedHash(document.classificationContentHash);
    setSelected(document.subjectMatters ?? []);
  }

  const contentHash = document.classificationContentHash;
  const trimmedReason = reason.trim();
  const canSave = Boolean(contentHash) && trimmedReason.length > 0 && !isLoading;
  const isQuarantined = document.isBeingProcessed || document.processingFailed;

  function toggle(subjectMatter: DocumentSubjectMatter, checked: boolean) {
    setSelected((current) =>
      checked
        ? [...current.filter((value) => value !== subjectMatter), subjectMatter].sort((a, b) => a - b)
        : current.filter((value) => value !== subjectMatter),
    );
  }

  async function handleSave() {
    if (!contentHash || trimmedReason.length === 0) return;
    setError(null);
    const result = await overrideClassification({
      documentId: document.id.value,
      labels: selected,
      contentHash,
      reason: trimmedReason,
    });
    if (!("error" in result)) {
      enqueueSnackbar("Subject matter saved", { variant: "success" });
      onClose();
      return;
    }

    const status = getApiErrorStatus(result.error);
    if (status === HttpStatusCode.Conflict) {
      setError(contentChangedMessage);
      return;
    }
    if (status === HttpStatusCode.Forbidden) {
      enqueueSnackbar("You can no longer change this document's subject matter.", { variant: "error" });
      onClose();
      return;
    }
    setError(getErrorMessageFromUnknown(result.error) ?? "The subject matter could not be saved. Try again.");
  }

  return (
    <Dialog open onClose={isLoading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Subject Matter</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          Subject matter decides who can read the content of {document.name.value}. A person needs
          access to every subject matter a document carries to read it.
        </DialogContentText>
        {isQuarantined && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {document.processingFailed
              ? "Processing failed, so this document's content is withheld. Saving its subject matter releases it."
              : "This document is still being processed. Saving its subject matter now releases its content, and the processing result is not applied."}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl component="fieldset" variant="standard" sx={{ mt: 1, mb: 2 }}>
          {/*
            A heading over the checkboxes, not one more line of the paragraph above: the legend's
            default is body text in the same grey, which read as a continuation of it.
          */}
          <SectionLabel component="legend">Subject matter</SectionLabel>
          <FormGroup>
            {subjectMatters.map((subjectMatter) => (
              <FormControlLabel
                key={subjectMatter}
                label={DocumentSubjectMatter[subjectMatter]}
                control={
                  <Checkbox
                    checked={selected.includes(subjectMatter)}
                    onChange={(event) => toggle(subjectMatter, event.target.checked)}
                  />
                }
              />
            ))}
          </FormGroup>
        </FormControl>
        <TextField
          label="Reason"
          required
          fullWidth
          multiline
          minRows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          helperText="Recorded in the document's history along with your name."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <LoadingButton variant="contained" onClick={handleSave} loading={isLoading} disabled={!canSave}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
